import dayjs from 'dayjs';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { doctorName, techName, type Visit } from './logic';

/**
 * Ghi ca thực hiện theo từng dịch vụ: ekip, mã máy, số shot.
 * API Lễ tân chỉ giữ một KTV, một bác sĩ, một phòng cho cả lượt, nên phần chi tiết tạm lưu localStorage
 * (chỉ thấy trên cùng trình duyệt). Có API thì thay store này, Kế toán vẫn đọc cùng dạng ExecRecord.
 */

export interface ExecLine { id: string; service: string; doctor: string; assistant: string; nurse: string; ktv: string; machine: string; shots?: number }
export interface ExecRecord {
  visitKey: string; serverId?: number; visitCode: string; date: string; phone: string; appointmentId?: number;
  room: string; at?: number; doneAt?: number;
  lines: ExecLine[];
  /** Lễ tân đã tự sửa: lần đọc lượt sau không điền lại người từ lượt */
  edited: boolean;
  updatedAt: number;
}

const uid = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
export const emptyLine = (service = '', doctor = '', ktv = ''): ExecLine => ({ id: uid(), service, doctor, assistant: '', nurse: '', ktv, machine: '' });

/**
 * Danh sách nhân sự chưa tải xong thì techName trả về chính mã, doctorName trả về gạch ngang.
 * Không ghi các giá trị đó vào bản ghi, vì Kế toán tính tua theo tên.
 */
const knownName = (id: string | undefined, nameOf: (id?: string) => string) => {
  if (!id) return '';
  const name = nameOf(id);
  return name === id || name === '—' ? '' : name;
};

/** Bản ghi theo lượt đang có: mỗi dịch vụ đăng ký một dòng, người lấy theo lượt. Trả về prev nếu không có gì đổi */
export function recordFromVisit(v: Visit, prev: ExecRecord | undefined, now: number): ExecRecord | undefined {
  if (!v.exec) return prev;
  const doctor = knownName(v.exec.doctor, doctorName);
  const ktv = knownName(v.exec.tech, techName);
  const services = (v.apptSvcs ?? []).map((s) => s.name);
  const lines = prev?.edited ? prev.lines
    : (services.length ? services : ['']).map((name, i) => {
      const old = prev?.lines[i];
      return old && old.service === name ? { ...old, doctor, ktv } : emptyLine(name, doctor, ktv);
    });
  const base = {
    visitKey: v.id, serverId: v.serverId, visitCode: v.code, date: dayjs(v.checkInAt ?? now).format('YYYY-MM-DD'), phone: v.phoneDigits ?? '', appointmentId: v.apiApptId,
    room: v.exec.room, at: v.exec.at, doneAt: v.exec.doneAt, lines, edited: !!prev?.edited,
  };
  if (prev) {
    const { updatedAt, ...rest } = prev;
    if (JSON.stringify(rest) === JSON.stringify(base)) return prev;
  }
  return { ...base, updatedAt: now };
}

/** Dòng còn thiếu người làm: Lễ tân thấy để ghi cho đủ */
export const lineMissing = (l: ExecLine) => !l.doctor && !l.ktv && !l.nurse;

interface ExecLogState {
  records: Record<string, ExecRecord>;
  syncVisit: (v: Visit) => void;
  saveLines: (visitKey: string, lines: ExecLine[]) => void;
}

export const useExecLog = create<ExecLogState>()(persist(
  (set, get) => ({
    records: {},
    syncVisit: (v) => {
      const prev = get().records[v.id];
      const next = recordFromVisit(v, prev, Date.now());
      if (next && next !== prev) set((s) => ({ records: { ...s.records, [v.id]: next } }));
    },
    saveLines: (visitKey, lines) => set((s) => {
      const cur = s.records[visitKey];
      return cur ? { records: { ...s.records, [visitKey]: { ...cur, lines, edited: true, updatedAt: Date.now() } } } : s;
    }),
  }),
  { name: 'khothi-exec-log-v1' },
));

// Lễ tân và Kế toán mở ở hai tab cùng trình duyệt: tab kia lưu thì đọc lại, Kế toán nhận ngay không chờ tải lại trang
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => { if (e.key === 'khothi-exec-log-v1') useExecLog.persist.rehydrate(); });
}
